/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import { theme } from "@nordcloud/gnui";
import { DataContainer, DataItem, EstatePeriod } from "~/components";
import { useCurrency } from "~/hooks";
import { formatMoney, isNotNil } from "~/tools";

type Props = {
  period: EstatePeriod;
  totalCost?: string;
  count?: number | null;
  monthlySavings?: string | null;
  isLoading?: boolean;
  forecast?: number;
  averageCost?: string;
};

export function AccumulatedCostData({
  period,
  totalCost,
  count,
  monthlySavings,
  isLoading = false,
  forecast,
  averageCost,
}: Props) {
  const { currency } = useCurrency();

  return (
    <DataContainer width="100%">
      <DataItem
        label="Total Cost"
        value={formatMoney(totalCost, currency)}
        grow={false}
        loading={isLoading}
        valueFontWeight={theme.fontWeights.medium}
        itemWidth="20%"
      />
      <If condition={period === EstatePeriod.RANGE}>
        <Then>
          <DataItem
            label="Average monthly cost"
            value={formatMoney(averageCost, currency)}
            grow={false}
            loading={isLoading}
            valueFontWeight={theme.fontWeights.medium}
            itemWidth="12.5%"
          />
        </Then>
        <Else>
          <DataItem
            label="Monthly Forecast"
            value={isNotNil(forecast) ? formatMoney(forecast, currency) : "-"}
            grow={false}
            loading={isLoading}
            valueFontWeight={theme.fontWeights.medium}
            itemWidth="12.5%"
          />
          <DataItem
            label="Potential Monthly Savings"
            grow={false}
            valueFontWeight={theme.fontWeights.medium}
            loading={isLoading}
            itemWidth="12.5%"
            value={
              isNotNil(monthlySavings)
                ? formatMoney(monthlySavings, currency)
                : "-"
            }
          />
          <DataItem
            label="Next 12 Months Savings"
            grow={false}
            valueFontWeight={theme.fontWeights.medium}
            loading={isLoading}
            itemWidth="12.5%"
            value={
              isNotNil(monthlySavings)
                ? formatMoney(Number(monthlySavings) * 12, currency)
                : "-"
            }
          />
          <DataItem
            label="Total Estate Records"
            value={isNotNil(count) ? count?.toString() : "-"}
            grow={false}
            valueFontWeight={theme.fontWeights.medium}
            loading={isLoading}
            itemWidth="12.5%"
          />
        </Else>
      </If>
    </DataContainer>
  );
}
